import React, { useEffect, useMemo, useState } from "react";
import { benchmarkRequestApis } from "../../api/benchmarkRequestApis";
import PeerExtractedTable from "./PeerExtractedTable";

function reportIdOf(req) {
  const id = req?.report ?? req?.reportId;
  if (!id) return "";
  if (typeof id === "object") return String(id._id || id.id || "");
  return String(id);
}

/**
 * Shows the latest completed peer extract for this report on the main
 * Industry Avg (etc.) dashboard. CTA to Request Peers when none exist.
 */
export default function PeerExtractPanel({ reportId, onOpenRequestPeers }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    benchmarkRequestApis
      .listMine()
      .then((rows) => {
        if (!cancelled) setRequests(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setRequests([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  const latest = useMemo(() => {
    if (!reportId) return null;
    const rid = String(reportId);
    const matches = requests
      .filter(
        (r) =>
          r.status === "completed" &&
          Array.isArray(r.peerExtracted) &&
          r.peerExtracted.length > 0 &&
          reportIdOf(r) === rid,
      )
      .sort(
        (a, b) =>
          new Date(b.completedAt || b.updatedAt || b.createdAt || 0) -
          new Date(a.completedAt || a.updatedAt || a.createdAt || 0),
      );
    return matches[0] || null;
  }, [requests, reportId]);

  if (loading) {
    return (
      <div className="benchmark-panel">
        <h2>Peer extract results</h2>
        <p className="benchmark-empty" style={{ margin: 0 }}>
          Loading peer extracts…
        </p>
      </div>
    );
  }

  if (!latest) {
    return (
      <div className="benchmark-panel">
        <h2>Peer extract results</h2>
        <p style={{ margin: "0 0 0.75rem", color: "#64748b", fontSize: "0.9rem" }}>
          No completed peer extracts for this report yet. Request Peers runs the
          automation and fills this table with company datapoints.
        </p>
        {typeof onOpenRequestPeers === "function" ? (
          <button
            type="button"
            className="benchmark-type-card"
            style={{ maxWidth: 280, textAlign: "left", cursor: "pointer" }}
            onClick={onOpenRequestPeers}
          >
            <strong>Open Request Peers</strong>
            <span style={{ display: "block", fontSize: "0.8rem", color: "#64748b" }}>
              Select companies and run extraction
            </span>
          </button>
        ) : null}
      </div>
    );
  }

  const peers = (latest.companies || []).map((c) => c.name).filter(Boolean);
  return (
    <div className="benchmark-panel">
      <h2>Peer extract results</h2>
      <p style={{ margin: "0 0 0.75rem", color: "#64748b", fontSize: "0.85rem" }}>
        Latest completed request
        {peers.length ? ` · ${peers.join(", ")}` : ""}
        {latest.completedAt
          ? ` · ${new Date(latest.completedAt).toLocaleString()}`
          : ""}
      </p>
      <PeerExtractedTable
        rows={latest.peerExtracted}
        title={`Extracted datapoints (${latest.peerExtracted.length})`}
      />
    </div>
  );
}
